'use strict';

import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, switchMap, takeUntil, catchError, filter, map } from "rxjs/operators";
import { of, Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';

// Importações de Serviços
import { ClienteService } from '../shared/cliente.service';
import { UfService } from '../../uf/shared/uf.service';
import { PaisService } from '../../pais/shared/pais.service';
import { MunicipioService } from '../../municipio/shared/municipio.service';
import { CepService } from '../../cep/shared/cep.service';
import { ContaContabilService } from '../../contabil/shared/conta-contabil.service';
import { ClienteServicoService } from '../shared/cliente-servico.service';
import { UtilsService } from 'src/app/core/utils/utils.service';
import { ClienteFaturamentoService } from '../shared/cliente-faturamento.service';
import { ContatoService } from 'src/app/contato/shared/contato.service';
import { DadoBancarioService } from 'src/app/dado-bancario/shared/dado-bancario.service';
import { ClienteContadorService } from '../shared/cliente-contador.service';
import { ImpostoService } from 'src/app/fiscal/shared/imposto.service';

// Importações de Componentes de Diálogo
import { ClienteServicoComponent } from '../cliente-servico/cliente-servico.component';
import { ClienteFaturamentoComponent } from '../cliente-faturamento/cliente-faturamento.component';
import { ContatoCadastroComponent } from 'src/app/contato/contato-cadastro/contato-cadastro.component';
import { DadoBancarioCadastroComponent } from 'src/app/dado-bancario/dado-bancario-cadastro/dado-bancario-cadastro.component';
import { InfoDialogComponent } from '../../core/dialogs/info-dialog/info-dialog.component';
import { ConfirmDialogComponent } from '../../core/dialogs/confirm-dialog/confirm-dialog.component';
import { ClienteHistoricoComponent } from '../cliente-historico/cliente-historico.component';

// Modelos e Interfaces
import { Cliente } from '../shared/cliente.model';
import { ClienteContador } from '../shared/cliente-contador';

export interface DialogData {
  clienteId: number;
  clienteTipo: number;
  clienteNome: string;
  clienteEmpresas: any[];
}

// --- INTERFACES AJUSTADAS PARA CLAREZA ---
export interface EmpresaDto {
  empresaId: number;
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
}

// Representa a resposta da API (a lista mestra )
export interface ImpostoMestre {
  impostoId: number;
  nomeImposto: string;
  empresas: EmpresaDto[];
}

// Representa uma linha na sua tabela
export interface ImpostoTabela {
  impostoId: number;
  tipoImposto: string;
  retido: boolean;
  empresasDisponiveis: EmpresaDto[];
  empresasSelecionadas: EmpresaDto[];
}
// --- FIM DAS INTERFACES ---

@Component({
  selector: 'app-cliente-cadastro',
  templateUrl: './cliente-cadastro.component.html',
})
export class ClienteCadastroComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  clienteCadastroForm: FormGroup;
  clienteContadorForm: FormGroup;

  maximizado = false;
  pessoaFisica = false;
  localEntregaAtivo = false;
  integracao = false;
  textoclienteTipo = '';

  ufs: any[] = [];
  paises: any[] = [];
  municipios: any[] = [];
  localEntregaMunicipios: any[] = [];
  contasContabeis: any[] = [];
  empresas: any[] = [];
  impostos: any[] = []; // Mantido para compatibilidade com seu código original
  contatos: any[] = [];
  dadosBancarios: any[] = [];
  clienteHistoricos: any[] = [];
  clienteServicos: any[] = [];
  municipio: any = null;
  dataSourceNfse = new MatTableDataSource<ImpostoTabela>();
  dataSourceNfe = new MatTableDataSource<ImpostoTabela>();
  displayedColumns: string[] = ['retido', 'tipoImposto', 'empresas'];

  constructor(
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<ClienteCadastroComponent>,
    private _formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public ICliente: DialogData,
    private clienteService: ClienteService,
    private clienteServicoService: ClienteServicoService,
    private municipioService: MunicipioService,
    private ufService: UfService,
    private paisService: PaisService,
    private cepService: CepService,
    private contaContabilService: ContaContabilService,
    private clienteFaturamentoService: ClienteFaturamentoService,
    private utilsService: UtilsService,
    private contatoService: ContatoService,
    private dadoBancarioService: DadoBancarioService,
    private clienteContadorService: ClienteContadorService,
    private impostoService: ImpostoService,
    private cdr: ChangeDetectorRef
  ) {
    this.clienteCadastroForm = this._formBuilder.group({
      ...new Cliente(),
      contaContabilAutoComplete: [''],
      clienteEmpresas: [[], Validators.required],
      clienteImpostosEmpresas: [[]], // Campo para guardar os dados salvos
      email: ['', [Validators.required, Validators.email]],
      municipioId: [null, Validators.required],
    });

    this.clienteContadorForm = this._formBuilder.group({
      ...new ClienteContador(),
      contadorCheckbox: [false]
    });
  }

  ngOnInit(): void {
    this.carregarDadosIniciais();
    this.registrarListenersDeFormulario();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarDadosIniciais(): void {
    this.buscarPaises();
    this.buscarUfs();
    this.buscarEmpresas();
    this.verificarIntegracaoSoc();

    if (this.ICliente.clienteId > 0) {
      this.buscarCliente(this.ICliente.clienteId);
    } else {
      this.textoclienteTipo = this.ICliente.clienteTipo === 1 ? 'Lead' : 'Cliente';
      this.clienteCadastroForm.patchValue({
        clienteTipo: this.ICliente.clienteTipo,
        razaoSocial: this.ICliente.clienteNome || '',
        clienteEmpresas: this.ICliente.clienteEmpresas || []
      });
    }
  }

  private registrarListenersDeFormulario(): void {
    this.clienteCadastroForm.get('contaContabilAutoComplete').valueChanges.pipe(debounceTime(300), filter(value => typeof value === 'string' && value.length >= 3), switchMap(value => this.contaContabilService.postVisualizaContaContabilPorFiltro(value).pipe(catchError(() => of({ body: [] })))), takeUntil(this.destroy$)).subscribe(data => this.contasContabeis = data.body || []);
    this.clienteCadastroForm.get('ufId').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(ufId => this.buscarMunicipioPorUf(ufId, false));
    this.clienteCadastroForm.get('ufEntregaId').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(ufId => this.buscarMunicipioPorUf(ufId, true));
    this.clienteCadastroForm.get('isento').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(isento => { const inscEstadualControl = this.clienteCadastroForm.get('inscEstadual'); if (isento) { inscEstadualControl.setValue('ISENTO'); inscEstadualControl.disable(); } else { inscEstadualControl.setValue(''); inscEstadualControl.enable(); } });
    this.clienteCadastroForm.get('pessoaFisica').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(pf => this.pessoaFisica = pf);
    this.clienteCadastroForm.get('localEntregaAtivo').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(ativo => this.localEntregaAtivo = ativo);
    this.clienteContadorForm.get('contadorCheckbox').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => { this.cdr.detectChanges(); });
  }

  private buscarCliente(clienteId: number): void {
    this.clienteService.getCliente(clienteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: data => this.preencherDadosCliente(data.body),
      error: err => this.handleApiError(err, "Erro ao buscar cliente")
    });
  }

  private preencherDadosCliente(cliente: any): void {
    if (!cliente) return;
    this.clienteCadastroForm.patchValue(cliente);
    this.textoclienteTipo = cliente.clienteTipo === 1 ? 'Lead' : 'Cliente';
    this.pessoaFisica = !cliente.cnpj;
    this.clienteCadastroForm.get('pessoaFisica').setValue(this.pessoaFisica);
    this.localEntregaAtivo = cliente.localEntregaAtivo;
    if (cliente.clienteContadorId) { this.clienteContadorService.getClienteContadorByClienteId(cliente.clienteId).pipe(takeUntil(this.destroy$)).subscribe(data => { if (data.body) { this.clienteContadorForm.patchValue(data.body); this.clienteContadorForm.get('contadorCheckbox').setValue(true); } }); }
  }

  matTabMudouAba(event: any): void {
    switch (event.index) {
      case 1: this.buscarImpostos(); break;
      case 2: this.buscarContatos(); break;
      case 3: this.buscarDadosBancarios(); break;
      case 4: this.buscarClienteHistoricos(); break;
      case 5: this.buscarClienteServicos(); break;
    }
  }

  // --- LÓGICA DE IMPOSTOS AJUSTADA ---

  private buscarImpostos(): void {
    if (this.dataSourceNfse.data.length > 0) return;

    this.impostoService.getFiltroImpostoEmpresaProduto(this.ICliente.clienteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: { body: ImpostoMestre[] }) => {
        this.impostos = res.body || [];
        const impostosSalvos = this.clienteCadastroForm.value.clienteImpostosEmpresas || [];
        this.preencherTabelasDeImpostos(impostosSalvos);
      },
      error: err => this.handleApiError(err, "Erro ao buscar impostos")
    });
  }

  private preencherTabelasDeImpostos(impostosSalvos: any[]): void {
    if (!this.impostos || this.impostos.length === 0) {
      return;
    }

    // A base para a tabela agora é a lista mestra de impostos
    const dadosBaseParaTabela = this.impostos.map((impostoMestre: ImpostoMestre) => ({
      impostoId: impostoMestre.impostoId,
      tipoImposto: impostoMestre.nomeImposto,
      retido: false,
      empresasDisponiveis: impostoMestre.empresas,
      empresasSelecionadas: []
    }));

    // Função para popular os dados com base nos valores salvos
    const popularDados = (dadosBase: ImpostoTabela[], tipoNota: number): ImpostoTabela[] => {
      const impostosAgrupados = this.agruparImpostosParaExibicao(impostosSalvos, tipoNota);

      return dadosBase.map(item => {
        const match = impostosAgrupados.find(x => x.ImpostoId === item.impostoId);

        if (match) {
          // Pega os objetos completos das empresas que foram salvas
          const empresasSelecionadasCompletas = item.empresasDisponiveis.filter(
            empresaDisponivel => match.Empresas.includes(empresaDisponivel.empresaId)
          );
          return { ...item, retido: match.Retido, empresasSelecionadas: empresasSelecionadasCompletas };
        }
        return item;
      });
    };

    this.dataSourceNfse.data = popularDados([...dadosBaseParaTabela], 1);
    this.dataSourceNfe.data = popularDados([...dadosBaseParaTabela], 0);
  }

  private agruparImpostosParaExibicao(impostosSalvos: any[], tipoNota: number): any[] {
    const impostosFiltrados = (impostosSalvos || []).filter(item => (item.tipoNota ?? item.TipoNota) === tipoNota);
    const agrupado = impostosFiltrados.reduce((acc, item) => {
      const impostoId = item.impostoId ?? item.ImpostoId;
      const retido = item.retido ?? item.Retido;
      const key = `${impostoId}`;
      if (!acc[key]) {
        acc[key] = { ImpostoId: impostoId, Retido: !!retido, Empresas: [] };
      }
      acc[key].Empresas.push(item.empresaId ?? item.EmpresaId);
      return acc;
    }, {});
    return Object.values(agrupado);
  }

  private mapearImpostosParaSalvar(): any[] {
    const impostosMapeados: any[] = [];
    const { clienteId, saguiClienteId } = this.clienteCadastroForm.value;
    const processarDataSource = (dataSource: MatTableDataSource<ImpostoTabela>, tipoNota: number) => {
      dataSource.data.forEach(item => {
        if (item.retido && item.empresasSelecionadas?.length > 0) {
          item.empresasSelecionadas.forEach(empresa => {
            impostosMapeados.push({
              EmpresaId: empresa.empresaId,
              ImpostoId: item.impostoId,
              Retido: item.retido,
              TipoNota: tipoNota,
              ClienteId: clienteId,
              SaguiClienteId: saguiClienteId,
            });
          });
        }
      });
    };
    processarDataSource(this.dataSourceNfse, 1);
    processarDataSource(this.dataSourceNfe, 0);
    return impostosMapeados;
  }

  // --- FIM DAS MUDANÇAS ---

  private buscarUfs(): void { this.ufService.getUFs().pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.ufs = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar UFs") }); }
  private buscarPaises(): void { this.paisService.getPaises().pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.paises = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar Países") }); }
  private buscarEmpresas(): void { this.clienteService.getEmpresasAllDados().pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.empresas = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar empresas") }); }
  private buscarMunicipioPorUf(ufId: number, localEntrega: boolean): void { if (!ufId) return; this.municipioService.getMunicipioPorUf(ufId).pipe(takeUntil(this.destroy$)).subscribe({ next: data => { if (localEntrega) { this.localEntregaMunicipios = data.body || []; } else { this.municipios = data.body || []; } }, error: err => this.handleApiError(err, "Erro ao buscar municípios") }); }
  private buscarContasContabeis(): void { const contaId = this.clienteCadastroForm.value.contaContabilId; if (contaId > 0 && !this.clienteCadastroForm.get('contaContabilAutoComplete').value) { this.contaContabilService.getContaContabil(contaId).pipe(takeUntil(this.destroy$)).subscribe(data => { if (data.body) { this.clienteCadastroForm.get('contaContabilAutoComplete').setValue(data.body.descricao); } }); } }
  private buscarContatos(): void { if (this.contatos.length > 0) return; this.clienteService.getContatos(this.ICliente.clienteId).pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.contatos = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar contatos") }); }
  private buscarDadosBancarios(): void { if (this.dadosBancarios.length > 0) return; this.clienteService.getDadosBancarios(this.ICliente.clienteId).pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.dadosBancarios = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar dados bancários") }); }
  private buscarClienteHistoricos(): void { if (this.clienteHistoricos.length > 0) return; this.clienteService.getClienteHistoricos(this.ICliente.clienteId).pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.clienteHistoricos = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar históricos") }); }
  private buscarClienteServicos(): void { if (this.clienteServicos.length > 0) return; this.clienteService.getClienteServicos(this.ICliente.clienteId).pipe(takeUntil(this.destroy$)).subscribe({ next: data => this.clienteServicos = data.body || [], error: err => this.handleApiError(err, "Erro ao buscar assinaturas") }); }
  private verificarIntegracaoSoc(): void { this.clienteService.verficaIntegracaoSeHaIntegrcaoSoc().pipe(takeUntil(this.destroy$)).subscribe(data => { this.integracao = data.body === true; }); }

  salvar(): void {
    if (this.clienteCadastroForm.invalid) {
      this.exibirSnackBar("Formulário inválido. Verifique os campos obrigatórios.", true); return;
    }

    if(this.clienteCadastroForm.get("pessoaFisica").value == true)
      this.clienteCadastroForm.get("cnpj").setValue("");

    const formValue = this.clienteCadastroForm.value;

    const clientePayload = { ...formValue };
    clientePayload.clienteEmpresas = formValue.clienteEmpresas.map((empresa: any) => ({ clienteId: formValue.clienteId, empresaId: empresa.empresaId }));
    clientePayload.clienteImpostosEmpresas = this.mapearImpostosParaSalvar();

    const serviceCall = formValue.clienteId > 0 ? this.clienteService.putCliente(clientePayload, formValue.clienteId) : this.clienteService.postCliente(clientePayload);

    serviceCall.pipe(takeUntil(this.destroy$)).subscribe({ next: (data) => { this.exibirSnackBar("Registro salvo com sucesso!");

      if (data.body) {
        this.clienteCadastroForm.patchValue(data.body);
        if (!this.ICliente.clienteId) {
          this.ICliente.clienteId = data.body.clienteId;
        }
    }
  }, error: (err) => this.handleApiError(err, "Erro ao salvar cliente") });

  if (this.clienteContadorForm.get('contadorCheckbox').value) {

    this.salvarClienteContador();

   } }
  private salvarClienteContador(): void { const contadorPayload = this.clienteContadorForm.value; contadorPayload.clienteId = this.clienteCadastroForm.value.clienteId; const serviceCall = contadorPayload.clienteContadorId > 0 ? this.clienteContadorService.patchClienteContador(contadorPayload) : this.clienteContadorService.postClienteContador(contadorPayload); serviceCall.pipe(takeUntil(this.destroy$)).subscribe({ next: (data) => { if (data.body) { this.clienteContadorForm.patchValue(data.body); this.clienteCadastroForm.get('clienteContadorId').setValue(data.body.clienteContadorId); this.exibirSnackBar("Dados do contador salvos com sucesso."); } }, error: (err) => this.handleApiError(err, "Erro ao salvar dados do contador") }); }
  fecharDialog(): void { this.dialogRef.close(); }
  restaurarDialog(): void { this.maximizado = false; }
  maximizarDialog(): void { this.maximizado = true; }
  buscarParceiro(): void { const form = this.clienteCadastroForm; const cnpj = this.utilsService.removeCaracteresEspeciais(form.get('cnpj').value); if (!cnpj) return; this.clienteService.consultaParceiro({ cnpj }).pipe(takeUntil(this.destroy$)).subscribe({ next: data => { if (data.body) { form.patchValue(data.body); } }, error: err => this.handleApiError(err, "Erro ao consultar parceiro.") }); }  
  buscarParceiroContador(): void { const form = this.clienteContadorForm; const cnpj = this.utilsService.removeCaracteresEspeciais(form.get('cnpj').value); if (!cnpj) return; this.clienteService.consultaParceiro({ cnpj }).pipe(takeUntil(this.destroy$)).subscribe({ next: data => { if (data.body) { form.patchValue(data.body); } }, error: err => this.handleApiError(err, "Erro ao consultar parceiro contador.") }); }
  determinarContaContabil(conta: any): void { if (conta) { this.clienteCadastroForm.get('contaContabilId').setValue(conta.contaContabilId); this.clienteCadastroForm.get('contaContabilAutoComplete').setValue(conta.descricao); } }
  modalContato(id: number): void { const dialogRef = this.dialog.open(ContatoCadastroComponent, { data: { contatoId: id, idOrigem: this.ICliente.clienteId, contatoTipo: 1, saguiClienteId: this.clienteCadastroForm.value.saguiClienteId }, maxHeight: '90vh', maxWidth: '90vw' }); dialogRef.afterClosed().pipe(filter(result => !!result), takeUntil(this.destroy$)).subscribe(() => { this.contatos = []; this.buscarContatos(); }); }
  confirmarExclusaoContato(contatoId: number): void { this.abrirDialogoConfirmacao("Deseja realmente excluir este contato?", () => { this.contatoService.deleteContato(contatoId).pipe(takeUntil(this.destroy$)).subscribe({ next: () => { this.exibirSnackBar("Contato excluído com sucesso."); this.contatos = this.contatos.filter(c => c.contatoId !== contatoId); }, error: err => this.handleApiError(err, "Erro ao excluir contato.") }); }); }
  modalDadoBancario(id: number): void { const dialogRef = this.dialog.open(DadoBancarioCadastroComponent, { data: { dadoBancarioId: id, idOrigem: this.ICliente.clienteId, dadoBancarioTipoOrigem: 1, saguiClienteId: this.clienteCadastroForm.value.saguiClienteId }, maxHeight: '90vh', maxWidth: '90vw' }); dialogRef.afterClosed().pipe(filter(result => !!result), takeUntil(this.destroy$)).subscribe(() => { this.dadosBancarios = []; this.buscarDadosBancarios(); }); }
  confirmarExclusaoDadoBancario(dadoBancarioId: number): void { this.abrirDialogoConfirmacao("Deseja realmente excluir este dado bancário?", () => { this.dadoBancarioService.deleteDadoBancario(dadoBancarioId).pipe(takeUntil(this.destroy$)).subscribe({ next: () => { this.exibirSnackBar("Dado bancário excluído com sucesso."); this.dadosBancarios = this.dadosBancarios.filter(d => d.dadoBancarioId !== dadoBancarioId); }, error: err => this.handleApiError(err, "Erro ao excluir dado bancário.") }); }); }
  ModalClienteHistorico(id: number): void { const dialogRef = this.dialog.open(ClienteHistoricoComponent, { data: { clienteHistoricoId: id, clienteId: this.ICliente.clienteId }, maxHeight: '90vh' }); dialogRef.afterClosed().pipe(filter(result => !!result), takeUntil(this.destroy$)).subscribe(() => { this.clienteHistoricos = []; this.buscarClienteHistoricos(); }); }
  ModalClienteServico(id: number): void { const dialogRef = this.dialog.open(ClienteServicoComponent, { data: { clienteServicoId: id, clienteId: this.ICliente.clienteId }, maxHeight: '90vh' }); dialogRef.afterClosed().pipe(filter(result => !!result), takeUntil(this.destroy$)).subscribe(() => { this.clienteServicos = []; this.buscarClienteServicos(); }); }
  confirmarExclusaoClienteServico(clienteServicoId: number): void { this.abrirDialogoConfirmacao("Tem certeza que deseja cancelar esta assinatura?", () => { this.clienteServicoService.deleteClienteServico(clienteServicoId).pipe(takeUntil(this.destroy$)).subscribe({ next: () => { this.exibirSnackBar("Assinatura cancelada com sucesso."); this.clienteServicos = []; this.buscarClienteServicos(); }, error: err => this.handleApiError(err, "Erro ao cancelar assinatura.") }); }); }
  private abrirDialogoConfirmacao(mensagem: string, onConfirm: () => void): void { const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: { titulo: "Confirmação", mensagem } }); dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => { if (result) { onConfirm(); } }); }
  private exibirSnackBar(mensagem: string, isError = false): void { this._snackBar.open(mensagem, "Fechar", { duration: 5000, panelClass: isError ? ['snackbar-error'] : ['snackbar-success'] }); }
  private exibirDialogoInfo(titulo: string, mensagem: string): void { this.dialog.open(InfoDialogComponent, { data: { titulo, erro: mensagem } }); }
  private handleApiError(error: HttpErrorResponse, defaultMessage: string): void { const message = error.error?.message || defaultMessage; this.exibirSnackBar(message, true); }

  trackByFn(index: number, item: any): any {

    return item.id || item.clienteServicoId || item.contatoId || item.dadoBancarioId || item.clienteHistoricoId || index;

  }

  public compararEmpresas(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.empresaId === o2.empresaId : o1 === o2;
  }

   consultarCep(cep: string, localEntrega: boolean): void {
        if (!cep || cep.length < 8) {
            return;
        }

        // Supondo que utilsService exista e faça isso
        const cepLimpo = this.utilsService.removeCaracteresEspeciais(cep);

        // 1. Busca os dados do endereço pelo CEP
        this.cepService.getDadosEndereco(cepLimpo).pipe(
            // Usamos switchMap para encadear a próxima chamada (buscar municípios)
            switchMap(response => {
                const endereco = response.body;
                if (!endereco || !endereco.uf) {
                    // Se a API de CEP falhar, lança um erro que será pego pelo bloco 'error'
                    throw new Error('CEP não encontrado ou dados de endereço incompletos.');
                }

                // 2. Encontra a UF na lista já carregada (this.ufs)
                const ufEncontrada = this.ufs.find(u => u.siglaUF === endereco.uf && u.paisId === 30);
                if (!ufEncontrada) {
                    throw new Error('O estado retornado pelo CEP não foi encontrado no sistema.');
                }

                // Pré-preenche os campos que já temos
                const form = this.clienteCadastroForm;
                const patchData = {
                    logradouro: endereco.logradouro,
                    bairro: endereco.bairro,
                    ufId: ufEncontrada.ufId,
                    paisId: 30
                };

                if (localEntrega) {
                    form.patchValue({
                        logradouroEntrega: patchData.logradouro,
                        bairroEntrega: patchData.bairro,
                        ufEntregaId: patchData.ufId
                    });
                } else {
                    form.patchValue(patchData);
                }

                // 3. AGORA, busca a lista de municípios para a UF encontrada
                return this.municipioService.getMunicipioPorUf(ufEncontrada.ufId).pipe(
                    // Passa o resultado da busca de municípios e o nome da cidade para o próximo passo
                    map(municipiosResponse => ({
                        municipios: municipiosResponse.body || [],
                        nomeCidadeCep: endereco.localidade // 'localidade' é o nome da cidade
                    }))
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: ({ municipios, nomeCidadeCep }) => {
                // 4. Encontra o município correspondente pelo NOME NORMALIZADO (AQUI ESTÁ A CORREÇÃO)
                const nomeCidadeCepNormalizado = this.normalizarString(nomeCidadeCep);
                const municipioEncontrado = municipios.find(m =>
                    this.normalizarString(m.descricao) === nomeCidadeCepNormalizado
                );

                const form = this.clienteCadastroForm;
                if (localEntrega) {
                    // Atualiza a lista de municípios para o dropdown de entrega
                    this.localEntregaMunicipios = municipios;
                    if (municipioEncontrado) {
                        // 5. FINALMENTE, define o municipioId correto no formulário
                        form.patchValue({ municipioEntregaId: municipioEncontrado.municipioId });
                    }
                } else {
                    // Atualiza a lista de municípios para o dropdown principal
                    this.municipios = municipios;
                    if (municipioEncontrado) {
                        // 5. FINALMENTE, define o municipioId correto no formulário
                        form.patchValue({ municipioId: municipioEncontrado.municipioId });
                    }
                }

                if (!municipioEncontrado) {
                    console.warn(`Aviso: O município "${nomeCidadeCep}" não foi selecionado automaticamente. O usuário precisará escolhê-lo na lista.`);
                }
            },
            error: (err) => {
                this.exibirDialogoInfo("Erro na Consulta de CEP", err.message || "Não foi possível processar o CEP. Preencha o endereço manualmente.");
            }
        });
    }

   private normalizarString(texto: string): string {
        if (!texto) {
            return '';
        }
        return texto
            .normalize("NFD") // Separa os acentos dos caracteres base
            .replace(/[\u0300-\u036f]/g, "") // Remove os diacríticos (acentos)
            .toLowerCase(); // Converte tudo para minúsculas
    }
}
